import { useCallback, useEffect, useRef } from "react";
import { FEEDBACK_MS } from "../config";
import { feedbackKind, newSubmissionId } from "../data/responses";
import { clearRuntimeStep, writeRuntime } from "../data/runtimeStore";

// The one step lifecycle for every context (live release, homework,
// assessment, teacher preview, practice):
//
//   screen produces a canonical response
//   → `submit` records it (gateway, or memory when nothing may be written)
//   → the answer says whether the learner moves on and what feedback policy
//     permits
//   → the learner sees that feedback, then the next step or the end.
//
// The screen never decides correctness or completion. An incorrect answer is
// handed in like any other; whether it moves the learner on is policy.
//
// `state` is the caller's activity state ({ phase: "activity", activity,
// attempt, stepIndex, verdict }); `setState` updates it. `run` performs a
// save with the caller's saving/retry handling. `finish(state, attempt)`
// returns the state after the last step.

// An incorrect answer that moves on stays on screen a little longer, so the
// learner can read that it was kept.
const holdFor = (kind) => (kind === "recorded" ? FEEDBACK_MS * 2 : FEEDBACK_MS);

// Whether the gateway moved the learner on, from the attempt it returned:
// a retried request reports `advanced: false` even when the first one did
// advance, so the attempt itself is the truth.
const movedOn = (attempt, stepIndex) =>
  attempt.completed === true ||
  (Number.isInteger(attempt.currentStep) && attempt.currentStep > stepIndex);

const useAttemptSteps = ({ state, setState, submit, run, finish, keepOnDevice = true }) => {
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const completeStep = useCallback(
    (response, blob = null) => {
      if (state.phase !== "activity" || state.verdict?.moving) return;
      const { activity, attempt, stepIndex } = state;
      const step = activity.steps[stepIndex];
      // Made once, outside the retried action: a retry after a lost answer
      // sends the same id, so the gateway records the submission once.
      const submissionId = newSubmissionId();
      run(async () => {
        const result = await submit(attempt, {
          stepId: step.id,
          submissionId,
          response,
          blob,
        });
        const next = result.attempt;
        const moved = movedOn(next, stepIndex);
        const kind = feedbackKind({ outcome: result.outcome, moved });
        if (!moved) {
          // Correct-until-correct: the learner stays and tries again. Every
          // earlier answer is already kept in the attempt's history.
          setState((s) =>
            s.attempt?.id !== attempt.id
              ? s
              : {
                  ...s,
                  attempt: next,
                  verdict: { stepId: step.id, kind, tries: (s.verdict?.tries || 0) + 1 },
                }
          );
          return;
        }
        if (keepOnDevice) clearRuntimeStep(attempt.id, step.id);
        setState((s) =>
          s.attempt?.id !== attempt.id
            ? s
            : { ...s, attempt: next, verdict: { stepId: step.id, kind, moving: true } }
        );
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
          setState((s) => {
            if (s.phase !== "activity" || s.attempt?.id !== attempt.id) return s;
            if (next.completed) return finish(s, next);
            return { ...s, stepIndex: next.currentStep, verdict: null };
          });
        }, holdFor(kind));
      });
    },
    [state, setState, submit, run, finish, keepOnDevice]
  );

  // Unfinished work on the current step (a guess, ink, a draft) is kept on
  // this tablet only, so a refresh returns to it. It is not handed in and
  // never becomes a submission.
  const saveProgress = useCallback(
    (runtime) => {
      if (state.phase !== "activity") return;
      const { activity, attempt, stepIndex } = state;
      const stepId = activity.steps[stepIndex].id;
      if (keepOnDevice) writeRuntime(attempt.id, stepId, runtime);
      setState((s) =>
        s.attempt?.id !== attempt.id
          ? s
          : { ...s, runtime: { ...(s.runtime || {}), [stepId]: runtime } }
      );
    },
    [state, setState, keepOnDevice]
  );

  return { completeStep, saveProgress };
};

export default useAttemptSteps;
